export interface TvSku {
  id: string;
  brand: string;
  model: string;
  year: number;
  viewableDiagonalIn: number;
  panelWidthM: number;
  panelHeightM: number;
  sourceUrl: string;
  custom?: false;
}

export interface CustomTvSku {
  id: 'custom';
  brand: 'Custom';
  model: 'Measured active picture';
  year: 0;
  panelWidthM: number;
  panelHeightM: number;
  custom: true;
}

export type TvChoice = TvSku | CustomTvSku;

const METRES_PER_INCH = 0.0254;
const ASPECT_NORMALISER = Math.sqrt(16 ** 2 + 9 ** 2);

export function panelSizeFromDiagonal(viewableDiagonalIn: number): { widthM: number; heightM: number } {
  if (!Number.isFinite(viewableDiagonalIn) || viewableDiagonalIn <= 0) {
    throw new RangeError('Viewable diagonal must be positive');
  }
  const diagonalM = viewableDiagonalIn * METRES_PER_INCH;
  return {
    widthM: (diagonalM * 16) / ASPECT_NORMALISER,
    heightM: (diagonalM * 9) / ASPECT_NORMALISER,
  };
}

function sku(
  row: Omit<TvSku, 'panelWidthM' | 'panelHeightM' | 'custom'>,
): TvSku {
  const { widthM, heightM } = panelSizeFromDiagonal(row.viewableDiagonalIn);
  return { ...row, panelWidthM: widthM, panelHeightM: heightM };
}

/**
 * Dimensions are the active 16:9 picture, derived from the manufacturer's exact viewable
 * diagonal—not the chassis or stand. Values remain unrounded internally so the TV can be
 * used as the calibration ruler. Samsung's published visible-area figures cross-check the
 * same dimensions to within one millimetre.
 */
export const TV_SKUS: readonly TvSku[] = [
  // Source: https://www.lg.com/us/newsroom/media-entertainment/lg-begins-us-rollout-of-2024-oled-evo-tv-lineup
  sku({ id: 'lg-oled55c4pua', brand: 'LG', model: 'OLED55C4PUA', year: 2024, viewableDiagonalIn: 54.6, sourceUrl: 'https://www.lg.com/us/newsroom/media-entertainment/lg-begins-us-rollout-of-2024-oled-evo-tv-lineup' }),
  // Source: https://www.lg.com/us/newsroom/media-entertainment/lg-begins-us-rollout-of-2024-oled-evo-tv-lineup
  sku({ id: 'lg-oled65c4pua', brand: 'LG', model: 'OLED65C4PUA', year: 2024, viewableDiagonalIn: 64.5, sourceUrl: 'https://www.lg.com/us/newsroom/media-entertainment/lg-begins-us-rollout-of-2024-oled-evo-tv-lineup' }),
  // Source: https://www.lg.com/us/newsroom/media-entertainment/lg-begins-us-rollout-of-2024-oled-evo-tv-lineup
  sku({ id: 'lg-oled77c4pua', brand: 'LG', model: 'OLED77C4PUA', year: 2024, viewableDiagonalIn: 76.7, sourceUrl: 'https://www.lg.com/us/newsroom/media-entertainment/lg-begins-us-rollout-of-2024-oled-evo-tv-lineup' }),
  // Source: https://www.lg.com/us/newsroom/media-entertainment/lg-begins-us-rollout-of-2024-oled-evo-tv-lineup
  sku({ id: 'lg-oled55g4', brand: 'LG', model: 'OLED55G4', year: 2024, viewableDiagonalIn: 54.6, sourceUrl: 'https://www.lg.com/us/newsroom/media-entertainment/lg-begins-us-rollout-of-2024-oled-evo-tv-lineup' }),
  // Source: https://www.lg.com/us/newsroom/media-entertainment/lg-begins-us-rollout-of-2024-oled-evo-tv-lineup
  sku({ id: 'lg-oled65g4', brand: 'LG', model: 'OLED65G4', year: 2024, viewableDiagonalIn: 64.5, sourceUrl: 'https://www.lg.com/us/newsroom/media-entertainment/lg-begins-us-rollout-of-2024-oled-evo-tv-lineup' }),
  // Source: https://www.lg.com/us/newsroom/media-entertainment/lg-begins-us-rollout-of-2024-oled-evo-tv-lineup
  sku({ id: 'lg-oled77g4wua', brand: 'LG', model: 'OLED77G4WUA', year: 2024, viewableDiagonalIn: 76.7, sourceUrl: 'https://www.lg.com/us/newsroom/media-entertainment/lg-begins-us-rollout-of-2024-oled-evo-tv-lineup' }),
  // Source: https://images.samsung.com/is/content/samsung/p6/common/energylabel/common-energylabel-qe55s90daexxu-productfiche.pdf
  sku({ id: 'samsung-qe55s90daexxu', brand: 'Samsung', model: 'QE55S90DAEXXU', year: 2024, viewableDiagonalIn: 54.6, sourceUrl: 'https://images.samsung.com/is/content/samsung/p6/common/energylabel/common-energylabel-qe55s90daexxu-productfiche.pdf' }),
  // Source: https://images.samsung.com/is/content/samsung/p6/common/energylabel/common-energylabel-qe65s90datxxu-productfiche.pdf
  sku({ id: 'samsung-qe65s90datxxu', brand: 'Samsung', model: 'QE65S90DATXXU', year: 2024, viewableDiagonalIn: 64.5, sourceUrl: 'https://images.samsung.com/is/content/samsung/p6/common/energylabel/common-energylabel-qe65s90datxxu-productfiche.pdf' }),
  // Source: https://images.samsung.com/is/content/samsung/p6/common/energylabel/common-energylabel-qe55qn90datxxu-productfiche.pdf
  sku({ id: 'samsung-qe55qn90datxxu', brand: 'Samsung', model: 'QE55QN90DATXXU', year: 2024, viewableDiagonalIn: 54.6, sourceUrl: 'https://images.samsung.com/is/content/samsung/p6/common/energylabel/common-energylabel-qe55qn90datxxu-productfiche.pdf' }),
  // Source: https://images.samsung.com/is/content/samsung/p6/common/energylabel/common-energylabel-qe65qn90datxxu-productfiche.pdf
  sku({ id: 'samsung-qe65qn90datxxu', brand: 'Samsung', model: 'QE65QN90DATXXU', year: 2024, viewableDiagonalIn: 64.5, sourceUrl: 'https://images.samsung.com/is/content/samsung/p6/common/energylabel/common-energylabel-qe65qn90datxxu-productfiche.pdf' }),
  // Source: https://www.sony.co.uk/electronics/support/televisions-projectors-oled-tvs-android-/k-65xr80/specifications
  sku({ id: 'sony-k-65xr80', brand: 'Sony', model: 'BRAVIA 8 K-65XR80', year: 2024, viewableDiagonalIn: 64.5, sourceUrl: 'https://www.sony.co.uk/electronics/support/televisions-projectors-oled-tvs-android-/k-65xr80/specifications' }),
  // Source: https://www.tcl.com/usca/content/dam/tcl/product/home-theater/q-class/qm7k/documents/spec-sheets/QM7K%20Specifications%20and%20Highlights.pdf
  sku({ id: 'tcl-65qm7k', brand: 'TCL', model: '65QM7K', year: 2025, viewableDiagonalIn: 64.5, sourceUrl: 'https://www.tcl.com/usca/content/dam/tcl/product/home-theater/q-class/qm7k/documents/spec-sheets/QM7K%20Specifications%20and%20Highlights.pdf' }),
  // Source: https://files.hisense-usa.com/download/f256442e2e564ef2
  sku({ id: 'hisense-65u8k', brand: 'Hisense', model: '65U8K', year: 2023, viewableDiagonalIn: 64.5, sourceUrl: 'https://files.hisense-usa.com/download/f256442e2e564ef2' }),
] as const;

export const CUSTOM_TV: CustomTvSku = {
  id: 'custom',
  brand: 'Custom',
  model: 'Measured active picture',
  year: 0,
  panelWidthM: panelSizeFromDiagonal(64.5).widthM,
  panelHeightM: panelSizeFromDiagonal(64.5).heightM,
  custom: true,
};

export function tvChoice(id: string, customWidthM?: number, customHeightM?: number): TvChoice {
  if (id !== CUSTOM_TV.id) return TV_SKUS.find((item) => item.id === id) ?? TV_SKUS[1]!;
  const width = Number(customWidthM);
  const height = Number(customHeightM);
  return {
    ...CUSTOM_TV,
    panelWidthM: Number.isFinite(width) && width > 0 ? width : CUSTOM_TV.panelWidthM,
    panelHeightM: Number.isFinite(height) && height > 0 ? height : CUSTOM_TV.panelHeightM,
  };
}
